import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LockKeyhole } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, patchJSON } from '@/lib/api'
import { useViewOnly } from '@/components/templates/app-shell'

interface SystemEmailSetting {
  kind: string
  label: string
  trigger: string
  editability: string
  enabled: boolean
  toggleable: boolean
  locked_reason: string | null
}

interface SettingsResponse {
  system_email_settings: SystemEmailSetting[]
}

export function SystemEmailSettingsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const settingsQuery = useQuery({
    queryKey: ['system-email-settings'],
    queryFn: () => getJSON<SettingsResponse>('/api/v1/system_email_settings'),
    retry: false,
  })
  const update = useMutation({
    mutationFn: ({ kind, enabled }: Pick<SystemEmailSetting, 'kind' | 'enabled'>) =>
      patchJSON<{ system_email_setting: SystemEmailSetting }>(
        `/api/v1/system_email_settings/${kind}`,
        { system_email_setting: { enabled } },
      ),
    onSuccess: ({ system_email_setting }) => {
      queryClient.setQueryData<SettingsResponse>(['system-email-settings'], (current) => ({
        system_email_settings: (current?.system_email_settings ?? []).map((entry) =>
          entry.kind === system_email_setting.kind ? system_email_setting : entry,
        ),
      }))
    },
  })

  return (
    <div className="w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <h1 className="text-heading-sm font-semibold text-foreground">System Emails</h1>
      </header>
      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground">Loading System Email settings...</p>
      ) : settingsQuery.isError ? (
        <Alert>System Email settings unavailable</Alert>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>System Email</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Editability</TableHead>
                  <TableHead className="w-56">Delivery</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settingsQuery.data?.system_email_settings.map((entry) => (
                  <TableRow
                    className={entry.toggleable ? undefined : 'bg-muted/30'}
                    key={entry.kind}
                  >
                    <TableCell className="font-medium">{entry.label}</TableCell>
                    <TableCell>{entry.trigger}</TableCell>
                    <TableCell>{entry.editability}</TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        {entry.toggleable && !viewOnly ? (
                          <Checkbox
                            aria-label={entry.label}
                            checked={entry.enabled}
                            disabled={update.isPending}
                            onCheckedChange={(checked) =>
                              update.mutate({ kind: entry.kind, enabled: checked === true })
                            }
                          />
                        ) : (
                          <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <p className="text-body-sm">
                          {entry.toggleable ? (entry.enabled ? 'On' : 'Off') : 'On · Required'}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
